import { Reducer } from 'redux';
import {
    EMAIL_MARKETING_CAMPAIGN_DELETE_SUCCESS,
    EMAIL_MARKETING_CAMPAIGN_FETCH_START,
    EMAIL_MARKETING_CAMPAIGN_FETCH_SUCCESS,
    EMAIL_MARKETING_CAMPAIGN_SAVE_START,
    EMAIL_MARKETING_CAMPAIGN_SAVE_SUCCESS,
    EMAIL_MARKETING_CAMPAIGNS_FETCH_START,
    EMAIL_MARKETING_CAMPAIGNS_FETCH_SUCCESS,
    EMAIL_MARKETING_CLEAR_ERROR,
    EMAIL_MARKETING_ERROR,
    EMAIL_MARKETING_KPIS_FETCH_START,
    EMAIL_MARKETING_KPIS_FETCH_SUCCESS,
    EMAIL_MARKETING_LOCATIONS_FETCH_START,
    EMAIL_MARKETING_LOCATIONS_FETCH_SUCCESS,
    EMAIL_MARKETING_SENDER_SAVE_SUCCESS,
    EMAIL_MARKETING_SENDERS_FETCH_START,
    EMAIL_MARKETING_SENDERS_FETCH_SUCCESS,
    EMAIL_MARKETING_TEMPLATES_FETCH_START,
    EMAIL_MARKETING_TEMPLATES_FETCH_SUCCESS,
    EmailMarketingAction
} from 'actions/emailMarketing/EmailMarketingActionsTypes';
import { OrganizationCampaign } from 'model/emailMarketing';
import { EmailMarketingState, EmailMarketingStatus } from './EmailMarketingState';

const initialState: EmailMarketingState = {
    status: EmailMarketingStatus.Initial,
    loadingLocations: false,
    loadingTemplates: false,
    loadingCampaigns: false,
    loadingCampaign: false,
    loadingKpis: false,
    loadingSenders: false,
    locationsLoaded: false,
    templatesLoaded: false,
    campaignsLoaded: false,
    saving: false,
    locations: [],
    templates: [],
    senders: [],
    campaigns: [],
    campaign: undefined,
    kpis: undefined
};

// Replaces the campaign in the list, or adds it first (newest first, like the API).
const upsertCampaign = (
    campaigns: Array<OrganizationCampaign>,
    campaign: OrganizationCampaign
): Array<OrganizationCampaign> =>
    campaigns.some(item => item.id === campaign.id)
        ? campaigns.map(item => (item.id === campaign.id ? campaign : item))
        : [campaign, ...campaigns];

export const emailMarketingReducer: Reducer<EmailMarketingState, EmailMarketingAction> = (
    state = initialState,
    action
) => {
    switch (action.type) {
        case EMAIL_MARKETING_ERROR:
            return {
                ...state,
                status: EmailMarketingStatus.Error,
                error: action.payload.error,
                // A failed first load shouldn't keep the screens spinning.
                locationsLoaded: state.locationsLoaded || state.loadingLocations,
                templatesLoaded: state.templatesLoaded || state.loadingTemplates,
                campaignsLoaded: state.campaignsLoaded || state.loadingCampaigns,
                loadingLocations: false,
                loadingTemplates: false,
                loadingCampaigns: false,
                loadingCampaign: false,
                loadingKpis: false,
                loadingSenders: false,
                saving: false
            };

        case EMAIL_MARKETING_CLEAR_ERROR:
            return { ...state, status: EmailMarketingStatus.Initial, error: undefined };

        case EMAIL_MARKETING_LOCATIONS_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingLocations: true };

        case EMAIL_MARKETING_LOCATIONS_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingLocations: false,
                locationsLoaded: true,
                locations: action.payload.locations
            };

        case EMAIL_MARKETING_TEMPLATES_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingTemplates: true };

        case EMAIL_MARKETING_TEMPLATES_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingTemplates: false,
                templatesLoaded: true,
                templates: action.payload.templates
            };

        case EMAIL_MARKETING_CAMPAIGNS_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingCampaigns: true };

        case EMAIL_MARKETING_CAMPAIGNS_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingCampaigns: false,
                campaignsLoaded: true,
                campaigns: action.payload.campaigns
            };

        case EMAIL_MARKETING_CAMPAIGN_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingCampaign: true };

        case EMAIL_MARKETING_CAMPAIGN_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingCampaign: false,
                campaign: action.payload.campaign
            };

        case EMAIL_MARKETING_CAMPAIGN_SAVE_START:
            return { ...state, status: EmailMarketingStatus.Fetching, saving: true };

        case EMAIL_MARKETING_CAMPAIGN_SAVE_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                saving: false,
                campaigns: upsertCampaign(state.campaigns, action.payload.campaign),
                // A duplicate is a new campaign; keep the one that is open.
                campaign:
                    state.campaign && state.campaign.id !== action.payload.campaign.id
                        ? state.campaign
                        : action.payload.campaign
            };

        case EMAIL_MARKETING_CAMPAIGN_DELETE_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                saving: false,
                campaigns: state.campaigns.filter(item => item.id !== action.payload.campaignId),
                campaign: state.campaign?.id === action.payload.campaignId ? undefined : state.campaign
            };

        case EMAIL_MARKETING_KPIS_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingKpis: true };

        case EMAIL_MARKETING_KPIS_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingKpis: false,
                kpis: action.payload.kpis
            };

        case EMAIL_MARKETING_SENDERS_FETCH_START:
            return { ...state, status: EmailMarketingStatus.Fetching, loadingSenders: true };

        case EMAIL_MARKETING_SENDERS_FETCH_SUCCESS:
            return {
                ...state,
                status: EmailMarketingStatus.FetchSuccess,
                loadingSenders: false,
                senders: action.payload.senders
            };

        case EMAIL_MARKETING_SENDER_SAVE_SUCCESS: {
            const { sender } = action.payload;
            const exists = state.senders.some(item => item.locationId === sender.locationId);
            return {
                ...state,
                senders: exists
                    ? state.senders.map(item => (item.locationId === sender.locationId ? sender : item))
                    : [...state.senders, sender],
                // Keep the campaign location picker in step with the sender.
                locations: state.locations.map(location =>
                    location.id === sender.locationId
                        ? {
                              ...location,
                              verifiedSender: sender.status === 'verified',
                              senderEmail: sender.status === 'verified' ? sender.senderEmail : null
                          }
                        : location
                )
            };
        }

        default:
            return state;
    }
};

export default emailMarketingReducer;
